/**
 * Utilitaire pour gérer le localStorage des affiliés avec expiration de 30 jours
 */

const STORAGE_DURATION_DAYS = 30;
const AFFILIATE_STORAGE_KEY = 'validatedAffiliateId';
const AFFILIATE_NAME_STORAGE_KEY = 'affiliateName';
const AFFILIATE_EXPIRY_KEY = 'affiliateExpiry';

export interface AffiliateStorageData {
  affiliateId: string;
  affiliateName?: string;
}

/**
 * Sauvegarde les données d'affiliation dans le localStorage avec expiration de 30 jours
 */
export function saveAffiliateToStorage(data: AffiliateStorageData): void {
  try {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + STORAGE_DURATION_DAYS * 24 * 60 * 60 * 1000);
    
    localStorage.setItem(AFFILIATE_STORAGE_KEY, data.affiliateId);
    localStorage.setItem(AFFILIATE_EXPIRY_KEY, expiryDate.getTime().toString());
    
    if (data.affiliateName) {
      localStorage.setItem(AFFILIATE_NAME_STORAGE_KEY, data.affiliateName);
    }
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des données d\'affiliation:', error);
  }
}

/**
 * Récupère les données d'affiliation depuis le localStorage
 * Retourne null si expiré ou inexistant
 */
export function getAffiliateFromStorage(): AffiliateStorageData | null {
  try {
    const affiliateId = localStorage.getItem(AFFILIATE_STORAGE_KEY);
    const expiryTimestamp = localStorage.getItem(AFFILIATE_EXPIRY_KEY);
    
    // Si pas d'ID ou pas de date d'expiration, retourner null
    if (!affiliateId || !expiryTimestamp) {
      return null;
    }
    
    // Vérifier si expiré
    const now = new Date().getTime();
    const expiry = parseInt(expiryTimestamp, 10);
    
    if (now > expiry) {
      // Expiré, nettoyer le localStorage
      clearAffiliateStorage();
      return null;
    }
    
    // Valide et non expiré
    const affiliateName = localStorage.getItem(AFFILIATE_NAME_STORAGE_KEY);
    
    return {
      affiliateId,
      affiliateName: affiliateName || undefined
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des données d\'affiliation:', error);
    return null;
  }
}

/**
 * Supprime les données d'affiliation du localStorage
 */
export function clearAffiliateStorage(): void {
  try {
    localStorage.removeItem(AFFILIATE_STORAGE_KEY);
    localStorage.removeItem(AFFILIATE_NAME_STORAGE_KEY);
    localStorage.removeItem(AFFILIATE_EXPIRY_KEY);
  } catch (error) {
    console.error('Erreur lors de la suppression des données d\'affiliation:', error);
  }
}

/**
 * Vérifie si les données d'affiliation sont encore valides
 */
export function isAffiliateStorageValid(): boolean {
  const data = getAffiliateFromStorage();
  return data !== null;
}
